import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Send, Plus, MessageSquare, Loader2, FileText, Eye, Radio, RefreshCw,
  PanelRightOpen, Sparkles, AlertTriangle, Trash2, Database, LayoutDashboard, Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Link, useNavigate } from "react-router-dom";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { UploadDataDialog } from "@/components/UploadDataDialog";
import { ChartRenderer } from "@/components/chat/ChartRenderer";
import { PlanPanel } from "@/components/chat/PlanPanel";
import { SaveReportDialog } from "@/components/reports/SaveReportDialog";
import { WatchMetricDialog } from "@/components/alerts/WatchMetricDialog";
import { PromoteSignalDialog } from "@/components/signals/PromoteSignalDialog";
import { AddToDashboardDialog } from "@/components/AddToDashboardDialog";
import { CHAT, BOARD, DECK } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import { streamChat } from "@/lib/sseChat";
import { EvidenceTrigger } from "@/components/evidence/EvidencePanel";
import api, { pingWarm } from "@/lib/api";

const SUGGESTIONS = [
  "What is the average occupancy rate this month versus last month?",
  "What is the 30 day readmission count by state?",
  "Which facility has the most hospital readmissions?",
  "Show the trend of daily occupancy rate over the last 60 days",
];

function MessageActions({ msg, workspaceId, onShowPlan, onRerun, onSaveReport, onWatch, onPromote, onAddToDashboard, onGeneratePresentation, question }) {
  const hasMetric = !!(msg.plan && (msg.plan.executed_sql || msg.plan.sql));
  const canSave = !!(hasMetric && msg.chart);
  const evidenceContext = !msg.evidence_answer_id && hasMetric
    ? {
        workspaceId,
        answerId: `chat-manual:${workspaceId}:${Date.now()}`,
        question: question || "",
        sql: msg.plan.executed_sql || msg.plan.sql || "",
        plan: msg.plan,
        chart: msg.chart || null,
        role: "viewer",
        restrictedColumns: [],
        rowCount: msg.result?.row_count ?? null,
        sourceKind: "chat",
      }
    : null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {msg.plan && (
        <Button variant="outline" size="sm" data-testid={CHAT.showPlan} onClick={() => onShowPlan(msg.plan)}
          className="h-7 rounded-full text-[11px]">
          <PanelRightOpen className="mr-1.5 h-3.5 w-3.5" /> Show plan
        </Button>
      )}
      <Button variant="outline" size="sm" data-testid={CHAT.rerun} onClick={onRerun} className="h-7 rounded-full text-[11px]">
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-run
      </Button>
      {(msg.evidence_answer_id || evidenceContext) && (
        <EvidenceTrigger
          answerId={msg.evidence_answer_id || null}
          context={evidenceContext}
        />
      )}
      {canSave && (
        <Button variant="outline" size="sm" data-testid={CHAT.saveReport} onClick={onSaveReport}
          className="h-7 rounded-full border-accent/40 text-[11px] text-accent hover:bg-accent/10">
          <FileText className="mr-1.5 h-3.5 w-3.5" /> Save as report
        </Button>
      )}
      {canSave && (
        <Button variant="outline" size="sm" data-testid={BOARD.addTrigger} onClick={onAddToDashboard}
          className="h-7 rounded-full border-accent/40 text-[11px] text-accent hover:bg-accent/10">
          <LayoutDashboard className="mr-1.5 h-3.5 w-3.5" /> Add to dashboard
        </Button>
      )}
      {hasMetric && (
        <Button variant="ghost" size="sm" data-testid={CHAT.watchMetric} onClick={onWatch} className="h-7 rounded-full text-[11px] text-muted-foreground">
          <Eye className="mr-1.5 h-3.5 w-3.5" /> Watch
        </Button>
      )}
      {canSave && (
        <Button variant="ghost" size="sm" data-testid={CHAT.promoteSignal} onClick={onPromote} className="h-7 rounded-full text-[11px] text-muted-foreground">
          <Radio className="mr-1.5 h-3.5 w-3.5" /> Promote to signal
        </Button>
      )}
    </div>
  );
}

function AssistantBody({ msg, onChip }) {
  return (
    <div data-testid={CHAT.assistantMessage} className="max-w-3xl">
      {msg.content && <p className="text-sm leading-relaxed text-foreground/90">{msg.content}</p>}

      {msg.refusal && (
        <div data-testid={CHAT.refusal} className="mt-3 rounded-xl border border-red-500/40 bg-red-500/5 p-4">
          <div className="flex items-center gap-2 text-sm text-red-400">
            <AlertTriangle className="h-4 w-4" /> Refused — restricted data
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{msg.refusal.message}</p>
        </div>
      )}

      {msg.clarification && (
        <div data-testid={CHAT.clarification} className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="text-sm text-foreground/90">{msg.clarification.question}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(msg.clarification.options || []).map((opt, i) => (
              <button
                key={i}
                data-testid={CHAT.clarificationChip}
                onClick={() => onChip(opt)}
                className="rounded-full border border-amber-500/40 bg-background/40 px-3 py-1.5 text-xs text-foreground/90 hover:border-amber-400 hover:bg-amber-500/10"
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {msg.error && (
        <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/5 p-4">
          <p className="text-sm text-red-400">{msg.error.message}</p>
          {msg.error.sql && <pre className="mt-2 overflow-auto rounded bg-background/60 p-2 text-[11px] text-muted-foreground">{msg.error.sql}</pre>}
          <Button variant="outline" size="sm" className="mt-2 h-7 text-[11px]" onClick={() => toast.info("Support request queued")}>
            Send to support
          </Button>
        </div>
      )}

      {msg.chart && <div className="mt-4">{<ChartRenderer chart={msg.chart} />}</div>}
    </div>
  );
}

export default function Chat() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streamMsg, setStreamMsg] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [input, setInput] = useState("");
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [deckBusy, setDeckBusy] = useState(false);
  // Prefill the composer from ?q= (e.g. onboarding sample questions) and
  // preselect a dataset from ?source= (e.g. after uploading into this workspace).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q) setInput(q);
    const s = params.get("source");
    if (s) setSourceId(s);
  }, []);

  const [busy, setBusy] = useState(false);
  const [saveCtx, setSaveCtx] = useState(null);
  const [watchCtx, setWatchCtx] = useState(null);
  const [promoteCtx, setPromoteCtx] = useState(null);
  const [dashCtx, setDashCtx] = useState(null);
  const [quotaBlock, setQuotaBlock] = useState(null);
  const [warming, setWarming] = useState(false);
  const accRef = useRef(null);
  const threadRef = useRef(null);
  const lastQuestionRef = useRef("");

  // Hand a problem statement to the autonomous deck agent, then open the deck.
  // `seed` (optional) preserves the exact chart the user is looking at as slide 0.
  const generateDeck = useCallback(async (prompt, seed = null) => {
    const text = (prompt || "").trim();
    if (!text || !active || deckBusy) return;
    setDeckBusy(true);
    try {
      const { data } = await api.post("/presentations", {
        workspace_id: active.id,
        prompt: text,
        source_id: sourceId,
        seed,
      });
      toast.success(seed ? "Building your deck with this chart as slide 1…" : "Building your presentation…");
      navigate(`/present/${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not start the presentation.");
    } finally {
      setDeckBusy(false);
    }
  }, [active, sourceId, deckBusy, navigate]);

  // Poll the lightweight warm probe on mount; show a subtle hint until the
  // embedding model is loaded so the first query after a cold boot feels expected.
  useEffect(() => {
    let active = true;
    let timer;
    const check = async () => {
      const { ready } = await pingWarm();
      if (!active) return;
      if (ready) { setWarming(false); return; }
      setWarming(true);
      timer = setTimeout(check, 2000);
    };
    check();
    return () => { active = false; clearTimeout(timer); };
  }, []);

  const loadConversations = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/chat/conversations", { params: { workspace_id: active.id } });
    setConversations(data);
  }, [active]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadSources = useCallback(async () => {
    if (!active) return;
    const { data } = await api.get("/sources", { params: { workspace_id: active.id } });
    setSources(data);
    setSourceId((prev) => {
      if (prev && data.find((s) => s.id === prev)) return prev;
      // Prefer a HEALTHY source (and the built-in demo/sample) so the chat never
      // defaults to a broken/most-recently-added source that errors on every query.
      const healthy = data.filter((s) => s.status === "healthy");
      const demo = healthy.find((s) => s.kind === "duckdb_demo" || s.kind === "sample");
      return (demo || healthy[0] || data[0])?.id || null;
    });
  }, [active]);

  useEffect(() => { loadSources(); }, [loadSources]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamMsg]);

  const openConversation = async (id) => {
    setActiveConvId(id);
    const { data } = await api.get(`/chat/conversations/${id}/messages`);
    setMessages(data);
    const lastPlan = [...data].reverse().find((m) => m.plan)?.plan;
    setSelectedPlan(lastPlan || null);
  };

  const newConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setSelectedPlan(null);
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/chat/conversations/${id}`);
    if (id === activeConvId) newConversation();
    loadConversations();
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    if (!active) {
      toast.info("Workspace still loading — try again in a moment.");
      return;
    }
    lastQuestionRef.current = q;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setBusy(true);
    const acc = { role: "assistant", content: "", plan: null, result: null, chart: null, clarification: null, refusal: null, error: null };
    accRef.current = acc;
    setStreamMsg({ ...acc });
    try {
      await streamChat({
        message: q,
        workspaceId: active.id,
        conversationId: activeConvId,
        sourceId,
        onEvent: (event, data) => {
          if (event === "meta") acc.meta = data;
          else if (event === "token") acc.content += data.text;
          else if (event === "plan") { acc.plan = data; setSelectedPlan(data); }
          else if (event === "result") acc.result = data;
          else if (event === "chart") acc.chart = data;
          else if (event === "clarification") acc.clarification = data;
          else if (event === "refusal") acc.refusal = data;
          else if (event === "error") acc.error = data;
          else if (event === "evidence") { acc.evidence_answer_id = data.answer_id; acc.evidence_id = data.id; }
          setStreamMsg({ ...acc });
        },
      });
    } catch (e) {
      if (e.status === 402) {
        setMessages((prev) => prev.slice(0, -1));
        setStreamMsg(null);
        setBusy(false);
        setQuotaBlock(e.detail || { message: e.message });
        return;
      }
      acc.error = { message: e.message };
    }
    setMessages((prev) => [...prev, { ...acc }]);
    setStreamMsg(null);
    setBusy(false);
    if (acc.meta?.conversation_id) {
      setActiveConvId(acc.meta.conversation_id);
      loadConversations();
    }
  };

  const ConversationList = () => (
    <div className="flex h-full flex-col">
      <Button data-testid={CHAT.newConversation} onClick={newConversation}
        className="mx-3 mt-3 rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90">
        <Plus className="mr-2 h-4 w-4" /> New chat
      </Button>
      <div className="mt-3 flex-1 overflow-y-auto px-2">
        {conversations.map((c) => (
          <div
            key={c.id}
            data-testid={CHAT.conversationItem}
            onClick={() => openConversation(c.id)}
            className={`group flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${
              c.id === activeConvId ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/40"
            }`}
          >
            <span className="flex items-center gap-2 truncate"><MessageSquare className="h-3.5 w-3.5 shrink-0" />{c.title}</span>
            <Trash2 className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
              onClick={(e) => deleteConversation(c.id, e)} />
          </div>
        ))}
        {!conversations.length && <p className="px-3 py-6 text-center text-xs text-muted-foreground">No conversations yet.</p>}
      </div>
    </div>
  );

  return (
    <div data-testid={CHAT.root} className="flex h-screen flex-col bg-background text-foreground font-mono">
      <WorkspaceNav />
      <div className="flex min-h-0 flex-1">
        {/* Left: conversations */}
        <aside className="hidden w-64 shrink-0 border-r border-border/60 md:flex md:flex-col">
          <ConversationList />
        </aside>

        {/* Center: thread */}
        <main className="flex min-w-0 flex-1 flex-col">
          {/* Mobile toolbar */}
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-2 md:hidden">
            <Sheet>
              <SheetTrigger asChild><Button variant="outline" size="sm" className="h-8 text-[11px]"><MessageSquare className="mr-1.5 h-3.5 w-3.5" />Chats</Button></SheetTrigger>
              <SheetContent side="left" className="w-72 p-0"><SheetHeader className="p-4"><SheetTitle>Conversations</SheetTitle></SheetHeader><ConversationList /></SheetContent>
            </Sheet>
            <Sheet>
              <SheetTrigger asChild><Button variant="outline" size="sm" className="h-8 text-[11px]"><PanelRightOpen className="mr-1.5 h-3.5 w-3.5" />Plan</Button></SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto p-0"><PlanPanel plan={selectedPlan} /></SheetContent>
            </Sheet>
          </div>

          <div ref={threadRef} data-testid={CHAT.thread} className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
            {sources.length > 1 && (
              <div className="mx-auto mb-4 flex max-w-4xl items-center gap-2">
                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Dataset</span>
                <Select value={sourceId || ""} onValueChange={setSourceId}>
                  <SelectTrigger data-testid={CHAT.sourcePicker} className="h-8 w-auto min-w-[200px] text-xs">
                    <SelectValue placeholder="Select a dataset" />
                  </SelectTrigger>
                  <SelectContent className="font-mono">
                    {sources.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {messages.length === 0 && !streamMsg ? (
              <div className="mx-auto max-w-2xl pt-10 text-center">
                <Sparkles className="mx-auto h-8 w-8 text-accent" />
                <h1 className="mt-5 text-2xl font-bold tracking-tight">Ask anything about your data</h1>
                <p className="mt-2 text-sm text-muted-foreground">Plain English in, the right chart out — with the SQL one click away.</p>
                <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid={CHAT.suggestion} onClick={() => send(s)}
                      className="rounded-xl border border-border/60 bg-card/40 p-4 text-left text-sm text-foreground/90 transition-colors hover:border-accent/50">
                      {s}
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex flex-col items-center gap-2">
                  <span className="text-xs text-muted-foreground">or bring your own data</span>
                  <UploadDataDialog workspaceId={active?.id}>
                    <button
                      data-testid={CHAT.uploadFile}
                      className="inline-flex items-center gap-2 rounded-full border border-accent/40 px-4 py-2 text-xs uppercase tracking-[0.14em] text-accent transition-colors hover:bg-accent/10"
                    >
                      <FileText className="h-3.5 w-3.5" /> Upload CSV / Parquet
                    </button>
                  </UploadDataDialog>
                </div>
              </div>
            ) : (
              <div className="mx-auto max-w-4xl space-y-6">
                {messages.map((m, i) =>
                  m.role === "user" ? (
                    <div key={i} className="flex justify-end">
                      <div data-testid={CHAT.userMessage} className="max-w-2xl rounded-2xl rounded-br-sm bg-accent/15 px-4 py-2.5 text-sm">{m.content}</div>
                    </div>
                  ) : (
                    <div key={i} className="flex flex-col">
                      <AssistantBody msg={m} onChip={(opt) => send(opt)} />
                      <MessageActions
                        msg={m}
                        workspaceId={active?.id}
                        question={messages[i - 1]?.content || lastQuestionRef.current}
                        onShowPlan={setSelectedPlan}
                        onRerun={() => send(lastQuestionRef.current || messages[i - 1]?.content)}
                        onSaveReport={() => setSaveCtx({
                          prompt: messages[i - 1]?.content || lastQuestionRef.current,
                          plan: m.plan,
                          chartType: m.chart?.type,
                        })}
                        onWatch={() => setWatchCtx({
                          prompt: messages[i - 1]?.content || lastQuestionRef.current,
                          plan: m.plan,
                        })}
                        onPromote={() => {
                          const prompt = messages[i - 1]?.content || lastQuestionRef.current;
                          const sql = m.plan?.executed_sql || m.plan?.sql;
                          const evidence = { sql, chart: m.chart || null };
                          if (m.chart?.type === "stat") {
                            evidence.value = m.chart.value;
                            if (m.chart.delta?.prior != null) evidence.baseline = m.chart.delta.prior;
                          }
                          setPromoteCtx({
                            name: (prompt || "Signal").slice(0, 90),
                            summary: m.content || "",
                            evidence,
                            ruleSnapshot: { prompt, source: "chat", tables_used: m.plan?.tables_used },
                          });
                        }}
                        onAddToDashboard={() =>
                          setDashCtx({
                            title: messages[i - 1]?.content || lastQuestionRef.current || "Visualization",
                            sourceId,
                            sql: m.plan?.executed_sql || m.plan?.sql,
                            chart: m.chart,
                            question: messages[i - 1]?.content || lastQuestionRef.current,
                          })
                        }
                        onGeneratePresentation={() => {
                          const q = messages[i - 1]?.content || lastQuestionRef.current;
                          const sql = m.plan?.executed_sql || m.plan?.sql;
                          generateDeck(q, {
                            question: q,
                            title: q?.slice(0, 80),
                            sql,
                            chart: m.chart || null,
                            columns: m.columns || null,
                            rows: (m.rows || []).slice(0, 500),
                          });
                        }}
                      />
                    </div>
                  )
                )}
                {streamMsg && (
                  <div className="flex flex-col">
                    <AssistantBody msg={streamMsg} onChip={(opt) => send(opt)} />
                    {busy && <div className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…</div>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-border/60 px-4 py-3 sm:px-8">
            <div className="mx-auto flex max-w-4xl items-end gap-2">
              <Textarea
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about occupancy, readmissions, payer mix, staffing…"
                rows={1}
                className="max-h-40 min-h-[44px] resize-none bg-card/60 text-sm"
              />
              <Button
                variant="outline"
                data-testid={DECK.composer}
                onClick={() => generateDeck(input)}
                disabled={deckBusy || !input.trim() || !active}
                title="Build a presentation from this problem statement"
                className="h-11 shrink-0 rounded-full border-accent/40 px-4 text-accent hover:bg-accent/10"
              >
                {deckBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Presentation className="h-4 w-4" />}
                <span className="ml-1.5 hidden text-xs uppercase tracking-[0.12em] sm:inline">Deck</span>
              </Button>
              <Button data-testid={CHAT.send} onClick={() => send()} disabled={busy || !input.trim() || !active}
                className="h-11 w-11 shrink-0 rounded-full bg-accent p-0 text-accent-foreground hover:bg-accent/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </main>

        {/* Right: plan panel */}
        <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-border/60 lg:block">
          <PlanPanel plan={selectedPlan} />
        </aside>
      </div>

      <SaveReportDialog
        open={!!saveCtx}
        onOpenChange={(o) => !o && setSaveCtx(null)}
        prompt={saveCtx?.prompt}
        plan={saveCtx?.plan}
        chartType={saveCtx?.chartType}
        workspaceId={active?.id}
      />

      <WatchMetricDialog
        open={!!watchCtx}
        onOpenChange={(o) => !o && setWatchCtx(null)}
        prompt={watchCtx?.prompt}
        plan={watchCtx?.plan}
        workspaceId={active?.id}
      />

      <PromoteSignalDialog
        open={!!promoteCtx}
        onOpenChange={(o) => !o && setPromoteCtx(null)}
        workspaceId={active?.id}
        source="chat"
        defaultName={promoteCtx?.name}
        defaultSummary={promoteCtx?.summary}
        evidence={promoteCtx?.evidence || {}}
        ruleSnapshot={promoteCtx?.ruleSnapshot || {}}
      />

      <AddToDashboardDialog
        open={!!dashCtx}
        onOpenChange={(o) => !o && setDashCtx(null)}
        ctx={dashCtx}
        workspaceId={active?.id}
      />

      <AlertDialog open={!!quotaBlock} onOpenChange={(o) => !o && setQuotaBlock(null)}>
        <AlertDialogContent data-testid="chat-quota-dialog" className="font-mono">
          <AlertDialogHeader>
            <AlertDialogTitle>You've hit your Sandbox limit</AlertDialogTitle>
            <AlertDialogDescription>
              {quotaBlock?.message || "Upgrade to Pro for unlimited chat queries, reports, and connectors."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <Link to="/settings/billing">
              <AlertDialogAction data-testid="chat-quota-upgrade" className="bg-accent text-accent-foreground hover:bg-accent/90">
                Upgrade plan
              </AlertDialogAction>
            </Link>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
